import styled from '@emotion/styled';
import { useEffect } from 'react';

import type { WalletRecipientView } from '@/api/address-book-bff/types';
import { Alert, forMobile } from '@/design-system';
import { getNormalizedSuiAddress } from '@/shared/modules/wallet/send/recipient';
import { CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import { SendAmountField } from './send-amount-field';
import { SendDivider } from './send-divider';
import { SendRecipientCombobox } from './send-recipient-combobox';
import { useSendRecipientSearch } from './use-send-recipient-search';

const Body = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[16],
  width: '100%',

  [forMobile]: {
    gap: theme.spacing[24],
  },
}));

export interface SendFormProps {
  assets: CryptoAsset[];
  coin: CryptoAsset;
  amount: string;
  amountUsd: number | null;
  amountError: string;
  recipientInput: string;
  selectedRecipient: WalletRecipientView | null;
  invalidRecipient: boolean;
  recipientHelper: string;
  disabledReason: string | null;
  onAmountChange: (_amount: string) => void;
  onCoinChange: (_coinType: string) => void;
  onRecipientInputChange: (_recipientInput: string) => void;
  onRecipientResolve?: (_address: string) => void;
  onRecipientSelect: (_recipient: WalletRecipientView) => void;
  onRecipientIdentityEnrich: (_recipient: WalletRecipientView) => void;
  onRecipientEdit: () => void;
  onRecipientClear: () => void;
  onRecipientBlur: () => void;
}

export const SendForm = ({
  assets,
  coin,
  amount,
  amountUsd,
  amountError,
  recipientInput,
  selectedRecipient,
  invalidRecipient,
  recipientHelper,
  disabledReason,
  onAmountChange,
  onCoinChange,
  onRecipientInputChange,
  onRecipientResolve = () => undefined,
  onRecipientSelect,
  onRecipientIdentityEnrich,
  onRecipientEdit,
  onRecipientClear,
  onRecipientBlur,
}: SendFormProps) => {
  const search = useSendRecipientSearch(selectedRecipient ? '' : recipientInput, (recipient) =>
    onRecipientResolve(recipient.address),
  );
  const currentAddress = getNormalizedSuiAddress(recipientInput);
  const matchedAddress = getNormalizedSuiAddress(search.addressMatch?.wallet.address ?? '');
  const showRecipientSearchState = currentAddress === null && selectedRecipient === null;
  const disabled = disabledReason !== null;

  useEffect(() => {
    if (selectedRecipient || !search.addressMatch || !currentAddress) return;
    if (currentAddress !== matchedAddress) return;

    onRecipientIdentityEnrich(search.addressMatch);
  }, [
    currentAddress,
    matchedAddress,
    onRecipientIdentityEnrich,
    search.addressMatch,
    selectedRecipient,
  ]);

  return (
    <Body>
      <SendAmountField
        assets={assets}
        coin={coin}
        amount={amount}
        amountUsd={amountUsd}
        error={amountError}
        onAmountChange={onAmountChange}
        onCoinChange={onCoinChange}
        amountDisabled={disabled}
      />
      {disabledReason ? <Alert variant="warning" description={disabledReason} /> : null}
      <SendDivider />
      <SendRecipientCombobox
        inputValue={recipientInput}
        selectedRecipient={selectedRecipient}
        recipients={search.recipients}
        isSearching={showRecipientSearchState && search.isSearching}
        showEmpty={showRecipientSearchState && search.showEmpty}
        showSearchError={showRecipientSearchState && search.showSearchError}
        invalid={invalidRecipient}
        helperText={recipientHelper}
        disabled={disabled}
        onInputChange={onRecipientInputChange}
        onSelect={onRecipientSelect}
        onEdit={onRecipientEdit}
        onClear={onRecipientClear}
        onBlur={onRecipientBlur}
      />
    </Body>
  );
};
